'use client';

import { useCallback, useEffect, useState } from 'react';

import Link from 'next/link';

import { LogoAnimated } from '@/components/common/logo-animated';
import { ArrowUpRightIcon } from '@/components/icons/svgs';

import { APP_ENVS } from '@/shared/constants/envs';

import { cn } from '@/utils/styles';
import { TEMPLATE_IDS, TemplateId, isValidTemplateId } from '@/utils/template-utils';

import { ITemplatePageLayoutProps, TemplateColor } from './template-page-layout.types';

// Color mapping for minimal template
const minimalTemplateBgMap: Record<TemplateColor, string> = {
	blue: 'from-[#F0F4FA] to-[#E8F0F7]',
	pink: 'from-[#FBEFF2] to-[#F5E6EA]',
	green: 'from-[#EEF7F2] to-[#E6F2ED]',
	orange: 'from-[#FEEBD4] to-[#FFF6EA]',
	purple: 'from-[#F4F1F7] to-[#EFEAF4]',
	gray: 'from-[#F3F4F6] to-[#E5E7EB]',
	red: 'from-[#FEE4E4] to-[#FEF0F0]',
};

// Map color names to their -100 hex value for the boxShadow
const themeShadowColorMap: Record<TemplateColor, string> = {
	blue: '#BFDBFE33',
	pink: '#fce7f3',
	green: '#BBF7D033',
	orange: '#FEEBD433',
	purple: '#DDD6FE33',

	gray: '#9CA3AF10',

	red: '#FEE4E433',
};

const CELL_WIDTH = 24;
const CELL_HEIGHT = 32;
const CONTAINER_WIDTH_MULTIPLIER = 32;
const CONTAINER_WIDTH = CONTAINER_WIDTH_MULTIPLIER * CELL_WIDTH + 1;

/**
 * The single source of truth for a form's template background — grid pattern or
 * minimal gradient. `mode="fixed"` pins it to the viewport for the full form-fill
 * page; `mode="absolute"` fills a positioned scroll container (e.g. the agent's
 * form preview) so it scrolls with the content. The grid is CSS-drawn so it tiles
 * to any size without measuring the window; the minimal gradient uses the shared
 * color map. Both TemplatePageLayout and the preview render through this.
 */
export function TemplateBackground({
	templateId = TEMPLATE_IDS.GRID,
	color = 'blue',
	mode = 'fixed',
	className,
}: {
	templateId?: TemplateId;
	color?: TemplateColor;
	mode?: 'fixed' | 'absolute';
	className?: string;
}) {
	const validTemplateId = isValidTemplateId(templateId) ? templateId : TEMPLATE_IDS.GRID;
	const positionClass = mode === 'fixed' ? 'fixed inset-0 z-0' : 'absolute inset-0';

	if (validTemplateId === TEMPLATE_IDS.MINIMAL) {
		return (
			<div
				aria-hidden
				className={cn(
					'pointer-events-none bg-gradient-radial',
					positionClass,
					minimalTemplateBgMap[color] || minimalTemplateBgMap.blue,
					className
				)}
			/>
		);
	}

	return (
		<div
			aria-hidden
			className={cn('pointer-events-none bg-slate-50', positionClass)}
			style={{
				backgroundImage:
					'linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)',
				backgroundSize: `${CELL_WIDTH}px ${CELL_HEIGHT}px`,
			}}
		/>
	);
}

function TemplatePageLayout({
	children,
	header,
	templateId = TEMPLATE_IDS.GRID,
	className,
	showWatermark = true,
	color = 'blue',
}: ITemplatePageLayoutProps) {
	const [isMobile, setIsMobile] = useState<boolean>(false);

	// Validate template ID and fallback to grid if invalid
	const validTemplateId = isValidTemplateId(templateId) ? templateId : TEMPLATE_IDS.GRID;

	useEffect(() => {
		const syncIsMobile = () => setIsMobile(window.innerWidth < 768);
		syncIsMobile();
		window.addEventListener('resize', syncIsMobile);

		return () => window.removeEventListener('resize', syncIsMobile);
	}, []);

	const getContainerStyles = useCallback(() => {
		switch (validTemplateId) {
			case TEMPLATE_IDS.GRID:
				return 'md:border';
			case TEMPLATE_IDS.MINIMAL:
				return 'rounded-md md:border md:border-white';
			default:
				return 'rounded-md md:border md:border-white';
		}
	}, [validTemplateId]);

	return (
		<div
			className={cn(
				'relative min-h-screen w-full bg-white md:bg-transparent',
				className && className
			)}
		>
			{/* Background — desktop only; mobile falls back to the plain white surface. */}
			{!isMobile && (
				<TemplateBackground
					templateId={validTemplateId}
					color={color}
					mode="fixed"
				/>
			)}

			{/* Header Section */}
			<div className="sticky top-0 z-50">{header}</div>

			{/* White Container */}
			<div
				className={cn(
					'relative z-10 h-auto bg-white md:my-[31px]',
					!isMobile && 'mx-auto',
					getContainerStyles()
				)}
				style={{
					width: isMobile ? '100%' : `${CONTAINER_WIDTH}px`,
					maxWidth: '100vw',
					...(validTemplateId === TEMPLATE_IDS.MINIMAL && !isMobile
						? { boxShadow: `0 0 16px 4px ${themeShadowColorMap[color] || '#dbeafe'}` }
						: {}),
				}}
			>
				{children}
			</div>

			{/* Watermark */}
			{showWatermark && (
				<div className="relative z-20 mb-8 mt-12 flex items-center justify-center md:mb-16 md:mt-4">
					<Link
						href={APP_ENVS.COMPANY_DOMAIN ?? ''}
						target="_blank"
						className="group"
					>
						<p className="ml-2 flex cursor-pointer items-center whitespace-nowrap text-sm font-medium text-primary">
							Made with
							<LogoAnimated className="mx-2 h-[28px] pb-1" />
							Binary
							<ArrowUpRightIcon className="invisible relative right-[3px] top-[1px] inline-block h-5 w-5 text-secondary transition-all group-hover:visible" />
						</p>
					</Link>
				</div>
			)}
		</div>
	);
}

export default TemplatePageLayout;
